import { useEffect, useState } from 'react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  PlayCircle,
  Save,
  Zap,
  Wrench,
  CheckCircle,
  Settings,
  HelpCircle,
  Workflow,
} from 'lucide-react';
import { toast } from 'sonner';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const { currentWorkflow, saveWorkflow, createWorkflow, workflows } = useWorkflowStore();
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleCreateWorkflow = () => {
    createWorkflow('New Workflow');
    setOpen(false);
    toast.success('New workflow created');
  };

  const handleSave = () => {
    saveWorkflow();
    setOpen(false);
    toast.success('Workflow saved');
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search workflows, actions, or use commands..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Workflow Actions */}
        <CommandGroup heading="Workflow">
          <CommandItem onSelect={handleCreateWorkflow}>
            <Plus className="mr-2 h-4 w-4" />
            Create New Workflow
          </CommandItem>
          <CommandItem onSelect={handleSave} disabled={!currentWorkflow}>
            <Save className="mr-2 h-4 w-4" />
            Save Workflow
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setOpen(false);
              navigate('/builder');
            }}
          >
            <Workflow className="mr-2 h-4 w-4" />
            Open Builder
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Node Templates */}
        <CommandGroup heading="Add Node">
          <CommandItem
            onSelect={() => {
              setOpen(false);
              toast.info('Right-click on canvas to add nodes');
            }}
          >
            <Zap className="mr-2 h-4 w-4 text-blue-600" />
            Trigger Node
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setOpen(false);
              toast.info('Right-click on canvas to add nodes');
            }}
          >
            <Wrench className="mr-2 h-4 w-4 text-amber-600" />
            Tool Node
          </CommandItem>
          <CommandItem
            onSelect={() => {
              setOpen(false);
              toast.info('Right-click on canvas to add nodes');
            }}
          >
            <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
            Action Node
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Recent Workflows */}
        {workflows.length > 0 && (
          <>
            <CommandGroup heading="Recent Workflows">
              {workflows.slice(0, 5).map((workflow) => (
                <CommandItem
                  key={workflow.id}
                  onSelect={() => {
                    useWorkflowStore.setState({ currentWorkflow: workflow });
                    setOpen(false);
                  }}
                >
                  <Workflow className="mr-2 h-4 w-4" />
                  {workflow.name}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Navigation */}
        <CommandGroup heading="Navigation">
          <CommandItem
            onSelect={() => {
              navigate('/dashboard');
              setOpen(false);
            }}
          >
            <Settings className="mr-2 h-4 w-4" />
            Dashboard
          </CommandItem>
          <CommandItem
            onSelect={() => {
              navigate('/help');
              setOpen(false);
            }}
          >
            <HelpCircle className="mr-2 h-4 w-4" />
            Documentation
          </CommandItem>
          <CommandItem
            onSelect={() => {
              navigate('/');
              setOpen(false);
            }}
          >
            <Workflow className="mr-2 h-4 w-4" />
            Home
          </CommandItem>
        </CommandGroup>
      </CommandList>

      <div className="border-t border-border p-2 text-xs text-muted-foreground">
        <p>Press <kbd className="bg-muted px-1 rounded">Ctrl K</kbd> (or <kbd className="bg-muted px-1 rounded">Cmd K</kbd>) to open</p>
      </div>
    </CommandDialog>
  );
}
